import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import {
  addByProducts37_3LCA,
  deleteByProducts37_3LCA,
  getAllByProducts37_3LCA,
  updateByProducts37_3LCA,
} from "../../../../../../apis/Product/InstallationConstructionFlow37Api";
import {
  getCurrencyDropdown,
  getProductMaterialUsage34_1aDropdown,
} from "../../../../../../apis/Dropdown";

const ByProducts37_3Table = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [componentDropdown, setComponentDropdown] = useState([]);
  const [currencyDropdown, setCurrencyDropdown] = useState([]);
  const [unitResources, setUnitResources] = useState({});

  useEffect(() => {
    getProductMaterialUsage34_1aDropdown({ setDropdown: setComponentDropdown });
    getCurrencyDropdown({ setDropdown: setCurrencyDropdown });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllByProducts37_3LCA({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && componentDropdown.length > 0) {
      const nextUnits = {};
      data.forEach((row) => {
        const code =
          typeof row.surplus_resource === "string"
            ? row.surplus_resource
            : row.surplus_resource?.value || row.surplus_resource?.code;
        if (code) {
          const found = componentDropdown.find(
            (opt) => !opt.isCategory && (opt.code === code || opt.value === code)
          );
          if (found) nextUnits[row.id] = found.unit || "xx";
        }
      });
      setUnitResources(nextUnits);
    }
  }, [data, componentDropdown]);

  // Convert currency code to currency name for display (if backend stores code)
  useEffect(() => {
    if (data.length > 0 && currencyDropdown.length > 0) {
      setData((prev) => {
        let changed = false;
        const converted = prev.map((row) => {
          if (!row.currency || typeof row.currency !== "string") return row;
          // If it's likely a currency code (3 letters), convert to name
          if (row.currency.length === 3) {
            const found = currencyDropdown.find(
              (c) => c.currency_code === row.currency || c.id === row.currency || c.code === row.currency
            );
            if (found && found.value && found.value !== row.currency) {
              changed = true;
              return { ...row, currency: found.value };
            }
          }
          return row;
        });
        return changed ? converted : prev;
      });
    }
  }, [data, currencyDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      surplus_resource: {
        targetField: "qty",
        getPlaceholder: (selectedValue, dropdownData, _rowData, rowId = "new") => {
          const selected = dropdownData?.find((opt) => opt?.value === selectedValue);
          if (selected) {
            setUnitResources((prev) => ({ ...prev, [rowId]: selected.unit || "xx" }));
          }
        },
        dropdownDataKey: "surplus_resource",
      },
    }),
    []
  );

  const getSurplusResourceCode = useCallback((field) => {
    if (!field) return null;
    if (typeof field === "string") return field;
    return field?.value || field?.code || null;
  }, []);

  const getCurrencyCode = useCallback(
    (currencyField) => {
      if (!currencyField) return null;
      const currencyValue =
        typeof currencyField === "string"
          ? currencyField
          : currencyField?.value || currencyField?.name || currencyField?.currency_name;

      const found = currencyDropdown.find(
        (c) =>
          c.value === currencyValue ||
          c.currency_name === currencyValue ||
          c.name === currencyValue ||
          c.currency_code === currencyValue ||
          c.code === currencyValue ||
          c.id === currencyValue
      );

      return found?.currency_code || found?.code || found?.id || null;
    },
    [currencyDropdown]
  );

  const normalizeByProductFlags = useCallback((row) => {
    const sold = row.by_product_sold === true;
    const usedThis = row.by_product_used_in_this_product === true;
    const usedOther = row.by_product_used_in_another_product === true;
    return { sold, usedThis, usedOther };
  }, []);

  const handleAdd = useCallback(
    (newItem) => {
      const resourceCode = getSurplusResourceCode(newItem.surplus_resource);
      const { sold, usedThis, usedOther } = normalizeByProductFlags(newItem);

      addByProducts37_3LCA({
        productId,
        surplus_resource: resourceCode,
        qty: newItem.qty || 0,
        by_product_sold: sold,
        by_product_used_in_this_product: usedThis,
        by_product_used_in_another_product: usedOther,
        monetary_value: sold ? newItem.monetary_value || null : null,
        currency: sold ? getCurrencyCode(newItem.currency) : null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData, getSurplusResourceCode, getCurrencyCode, normalizeByProductFlags]
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const resourceCode = getSurplusResourceCode(editedItem.surplus_resource);
      const { sold, usedThis, usedOther } = normalizeByProductFlags(editedItem);

      updateByProducts37_3LCA({
        productId,
        rowId: editedItem.id,
        surplus_resource: resourceCode,
        qty: editedItem.qty || 0,
        by_product_sold: sold,
        by_product_used_in_this_product: usedThis,
        by_product_used_in_another_product: usedOther,
        monetary_value: sold ? editedItem.monetary_value || null : null,
        currency: sold ? getCurrencyCode(editedItem.currency) : null,
        objects: data,
        setObjects: setData,
      });
    },
    [productId, data, getSurplusResourceCode, getCurrencyCode, normalizeByProductFlags]
  );

  const handleDelete = useCallback(
    (row) => {
      deleteByProducts37_3LCA({
        productId,
        rowId: row.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  // Disable rules to enforce single selection among the 3 checkboxes and lock monetary fields unless sold
  const disableRules = useMemo(
    () => ({
      by_product_sold: (rowData) =>
        (rowData.by_product_used_in_this_product || rowData.by_product_used_in_another_product) &&
        rowData.by_product_sold !== true,
      by_product_used_in_this_product: (rowData) =>
        (rowData.by_product_sold || rowData.by_product_used_in_another_product) &&
        rowData.by_product_used_in_this_product !== true,
      by_product_used_in_another_product: (rowData) =>
        (rowData.by_product_sold || rowData.by_product_used_in_this_product) &&
        rowData.by_product_used_in_another_product !== true,
      monetary_value: (rowData) => rowData.by_product_sold !== true,
      currency: (rowData) => rowData.by_product_sold !== true,
    }),
    []
  );

  const columns = useMemo(
    () => [
      {
        id: "surplus_resource",
        accessor: "surplus_resource",
        header: `${t("SURPLUS_RESOURCES") || "Surplus Resources"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_RESOURCE") || "Select resource...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "surplus_resource",
            componentDropdown,
            unitResources,
            rowId
          ),
      },
      {
        id: "qty",
        accessor: "qty",
        header: `${t("QTY_FUNC_UNIT") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "surplus_resource",
            componentDropdown,
            unitResources,
            rowId
          ),
      },
      {
        id: "by_product_sold",
        accessor: "by_product_sold",
        header: t("BY_PRODUCT_SOLD") || "By-product sold",
        type: "checkbox",
        optional: true,
      },
      {
        id: "by_product_used_in_this_product",
        accessor: "by_product_used_in_this_product",
        header: t("BY_PRODUCT_USED_IN_THIS_PRODUCT") || "By-product used in this product",
        type: "checkbox",
        optional: true,
      },
      {
        id: "by_product_used_in_another_product",
        accessor: "by_product_used_in_another_product",
        header:
          t("BY_PRODUCT_USED_IN_ANOTHER_PRODUCT") ||
          "By-product used internally but in another product",
        type: "checkbox",
        optional: true,
      },
      {
        id: "monetary_value",
        accessor: "monetary_value",
        header: t("MONETARY_VALUE") || "Monetary value",
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: "currency",
        accessor: "currency",
        header: t("CURRENCY") || "Currency",
        type: "dropdown",
        placeholder: t("SELECT_CURRENCY") || "Select currency...",
        optional: true,
      },
    ],
    [t, unitResources, componentDropdown, data]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      surplus_resource: componentDropdown || [],
      currency: currencyDropdown || [],
    }),
    [componentDropdown, currencyDropdown]
  );

  return (
    <div>
      <CustomTable
        columns={columns}
        data={data}
        setData={setData}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        dropdownData={memoizedDropdownData}
        hiddenColumns={hiddenColumns}
        toggleColumnVisibility={toggleColumnVisibility}
        disableRules={disableRules}
        dynamicPlaceholders={dynamicPlaceholders}
      />
    </div>
  );
};

export default ByProducts37_3Table;

